import React, { useState } from 'react';
import { organisationsAPI } from '../../../organisations/api/organisationsAPI';
import { organisationErrorMessage } from '../../../organisations/lib/errorMessage';
import { COMPLETION_KINDS } from '../../lib/completions';
import { DOCUMENT_TYPES, documentsHeld } from '../../lib/documents';
import { shortDate } from '../../lib/format';

/**
 * The cells the Overview's drill-downs draw beyond plain text: Most
 * engaged's mix of work, the documents view's "done" indicator and its
 * document cells. Used by the column sets in lib/studentTable.js.
 */

// Most engaged's mix: the seven completion kinds in five groups, coloured
// from the dashboard's own greens, amber and clay.
export const MIX_GROUPS = [
  { key: 'papers', label: 'Practice and papers', kinds: ['practice', 'papers'] },
  { key: 'quiz', label: 'Quiz questions', kinds: ['quiz'] },
  { key: 'writing', label: 'Writing', kinds: ['writing'] },
  { key: 'interviews', label: 'Interviews', kinds: ['interviews'] },
  { key: 'cases', label: 'Cases and mastery', kinds: ['cases', 'mastery'] },
];

const kindLabels = new Map(COMPLETION_KINDS.map((k) => [k.key, k.label]));

/** The groups' shares of a student's completions, in MIX_GROUPS order; empty groups left out. */
export const mixOf = (completions) => {
  const counts = MIX_GROUPS.map((g) => ({
    ...g,
    n: g.kinds.reduce((total, k) => total + (Number(completions?.[k]) || 0), 0),
  }));
  const total = counts.reduce((sum, g) => sum + g.n, 0);
  return { total, groups: counts.filter((g) => g.n > 0).map((g) => ({ ...g, pct: (g.n / total) * 100 })) };
};

export const MixBar = ({ completions }) => {
  const { total, groups } = mixOf(completions);
  if (!total) return <span className="td-mix td-mix--empty" aria-label="No completed work" />;
  const described = COMPLETION_KINDS
    .filter((k) => Number(completions?.[k.key]) > 0)
    .map((k) => `${kindLabels.get(k.key)} ${completions[k.key]}`)
    .join(', ');
  return (
    <span className="td-mix" role="img" aria-label={described} title={described}>
      {groups.map((g) => (
        <i key={g.key} className={`td-mix__part td-mix__part--${g.key}`} style={{ width: `${g.pct}%` }} />
      ))}
    </span>
  );
};

export const MixLegend = () => (
  <div className="td-mix-legend" aria-hidden="true">
    {MIX_GROUPS.map((g) => <span key={g.key} className={`td-mix-legend__item td-mix-legend__item--${g.key}`}>{g.label}</span>)}
  </div>
);

/** Three segments, one per document type, filled when that type is on file. */
export const DoneDots = ({ row }) => {
  const held = documentsHeld(row);
  return (
    <span className="td-done" role="img" aria-label={`${held} of ${DOCUMENT_TYPES.length} on file`}>
      {DOCUMENT_TYPES.map((t) => (
        <i key={t.key} className={row?.documents?.[t.key] ? 'td-done__on' : undefined} />
      ))}
    </span>
  );
};

/**
 * Opens one of the student's documents in a new tab. The window is opened
 * at once, on the click, as about:blank with no opener, and sent to the
 * short-lived link once GET .../documents/<id>/link answers: a window opened
 * after an await would be stopped by pop-up blockers. On a refusal the
 * blank window is closed and the reason is shown in the cell.
 */
/**
 * Opens one builder draft the same way as a tracker file: a blank tab on
 * the click, then the short-lived link. Each click asks for a new link.
 */
export const openBuilderDocument = async (studentId, userDocumentId) => {
  const win = window.open('about:blank', '_blank');
  if (!win) throw new Error('popup');
  win.opener = null;
  try {
    const { url } = await organisationsAPI.getBuilderDocumentLink(studentId, userDocumentId);
    win.location.href = url;
  } catch (err) {
    win.close();
    throw err;
  }
};

export const openDocument = async (studentId, documentId) => {
  const win = window.open('about:blank', '_blank');
  if (!win) throw new Error('popup');
  win.opener = null;
  try {
    const { url } = await organisationsAPI.getDocumentLink(studentId, documentId);
    win.location.href = url;
  } catch (err) {
    win.close();
    throw err;
  }
};

/** What to show when openDocument fails: pop-ups blocked, or the server's reason. */
export const openDocumentErrorMessage = (err) => (err?.message === 'popup'
  ? 'Allow pop-ups for this site to open documents.'
  : organisationErrorMessage(err, 'Could not open this document. Please try again.'));

const earlierText = (n) => (n > 0 ? ` · ${n} earlier` : '');

export const DocumentCell = ({ studentId, studentName, type, doc }) => {
  const [error, setError] = useState('');
  const [opening, setOpening] = useState(false);

  if (!doc) return <span className="td-doc td-doc--missing">Not uploaded</span>;

  const open = async () => {
    setError('');
    setOpening(true);
    try {
      await openDocument(studentId, doc.tracker_document_id);
    } catch (err) {
      setError(openDocumentErrorMessage(err));
    } finally {
      setOpening(false);
    }
  };

  return (
    <span className="td-doc">
      <button
        type="button"
        className="td-doc__open td-linkbtn"
        onClick={open}
        disabled={opening}
        title={doc.filename || undefined}
        aria-label={`Open ${studentName}'s ${type.noun}, version ${doc.version}, in a new tab`}
      >
        {`Version ${doc.version}`}
        <span aria-hidden="true"> ↗</span>
      </button>
      <small className="td-doc__meta">{`${shortDate(doc.created_at)}${earlierText(doc.earlier)}`}</small>
      {error ? <small className="td-doc__error" role="alert">{error}</small> : null}
    </span>
  );
};
